import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "../Accets/logo.png";

import ProductsMenu from "./Navbar/ProductsMenu";
import SolutionsMenu from "./Navbar/SolutionsMenu";
import ServicesMenu from "./Navbar/ServicesMenu";
import MobileMenu from "./Navbar/MobileMenu";
import DevelopersHubMenu from "./Navbar/DevelopersHubMenu";
import { useSmoothScroll, CustomCursor, PageLoader, MagneticButton } from "./motion/ScrollFX";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/work", label: "Work" },
];

// ---------------------------------------------------------------------
// Theme tokens — mid-tone slate, single confident accent. Deliberately
// restrained: one accent color, one signature move, no ambient effects.
// ---------------------------------------------------------------------
const theme = {
  navBg: "#6B1620",
  navBgScrolled: "#4E0F17",
  border: "rgba(255, 255, 255, 0.1)",
  accent: "#E63946",
  text: "#FFF8F6",
  textMuted: "#D8B4B8",
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Site-wide effects — Navbar is the one component mounted on every
  // route, so it's the right place to turn these on once: native smooth
  // scrolling, the custom pointer, and the one-time load-in screen.
  useSmoothScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const onKeyDown = (e) => e.key === "Escape" && setIsOpen(false);
      window.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onKeyDown);
      };
    }
  }, [isOpen]);

  return (
    <>
      <PageLoader logo={Logo} />
      <CustomCursor />

      <nav
        style={{
          backgroundColor: scrolled ? theme.navBgScrolled : theme.navBg,
          borderBottom: scrolled ? `1px solid ${theme.border}` : "1px solid transparent",
          boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
          fontFamily: "'Inter', sans-serif",
        }}
        className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl transition-all duration-300"
      >
        {/* Nav compacts on scroll — padding and logo shrink together,
           rather than only the background color changing, so the whole
           bar visibly tightens as you scroll (the RPS nav does the same). */}
        <div
          className={`max-w-7xl mx-auto px-6 flex justify-between items-center transition-all duration-300 ${
            scrolled ? "py-2.5" : "py-4"
          }`}
        >
          {/* Logo */}
          <Link to="/" data-cursor="hover" className="shrink-0 transition-transform hover:scale-[1.03]">
            <img
              src={Logo}
              alt="UnibComp Logo"
              className={`w-auto transition-all duration-300 ${scrolled ? "h-9" : "h-12"}`}
            />
          </Link>

          {/* Desktop Menu */}
          <ul
            className="hidden md:flex items-center gap-9 text-[14.5px] font-medium"
            style={{ letterSpacing: "0.01em" }}
          >
            {navLinks.map(({ to, label }) => (
              <li key={to}>
                <SignalLink to={to} label={label} active={location.pathname === to} />
              </li>
            ))}


            <ProductsMenu />
            <SolutionsMenu />
            <ServicesMenu />
            <DevelopersHubMenu />

            <li>
              <SignalLink
                to="/careers"
                label="Careers"
                active={location.pathname === "/careers"}
              />
            </li>

            <li>
              <MagneticButton strength={0.3}>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent, color: "#FFFFFF" }}
                  className={`inline-block rounded-md font-semibold text-[14.5px] transition-all hover:brightness-110 ${
                    scrolled ? "px-4 py-2" : "px-5 py-2.5"
                  }`}
                >
                  Contact Us
                </Link>
              </MagneticButton>
            </li>
          </ul>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            style={{ color: theme.text }}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <MobileMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      </nav>
    </>
  );
}

// Signature nav-link: the active page carries a small solid dot — the
// same visual language a network-status dashboard uses for "connected" —
// and every link gets a plain underline that scales in from the center
// on hover. One quiet signature, not three competing ones.
function SignalLink({ to, label, active }) {
  return (
    <Link
      to={to}
      data-cursor="hover"
      style={{ color: active ? theme.text : theme.textMuted }}
      className="group relative py-1 flex items-center gap-2 transition-colors hover:text-[#FFF8F6]"
    >
      {active && (
        <span
          style={{ backgroundColor: theme.accent }}
          className="w-[6px] h-[6px] rounded-full shrink-0"
        />
      )}
      <span className="relative">
        {label}
        <span
          style={{ backgroundColor: theme.accent }}
          className={`absolute left-0 -bottom-1 h-[1.5px] w-full origin-center transition-transform duration-300 ${
            active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
          }`}
        />
      </span>
    </Link>
  );
}